import React from 'react';
import {
  AssemblyType,
  AssemblyTypeKey,
  RpCheck2Type,
  TestRepair,
  TestResult,
  TestResultDc,
  TestResultRp,
  TestResultSvbPvb,
  TestResultType2Bypass,
} from '@ecosconnect/core/src/types/jobFormData';
import Accordion from '../../Accordion/Accordion';
import AccordionItem from '../../Accordion/AccordionItem';
import { validateTestResult } from '@ecosconnect/core/src/helpers/backflow/validate';
import { testValuesForType } from '@ecosconnect/core/src/helpers/backflow/evaluate';
import RpTestResult from './types/RpTestResult';
import DcTestResult from './types/DcTestResult';
import SvbPvbTestResult from './types/SvbPvbTestResult';
import Type2BypassTestResult from './types/Type2BypassTestResult';
import BackflowTestResultStatus from './BackflowTestResultStatus';
import backflowTestResultTitle from './backflowTestResultTitle';
import { Text } from 'rebass';

type BackflowTestResultProps = {
  test: TestResult | TestRepair;
  assemblyType: AssemblyTypeKey;
  rpCheck2Type: RpCheck2Type;
  index: number;
};

const BackflowTestResult: React.FC<BackflowTestResultProps> = ({
  test,
  assemblyType,
  rpCheck2Type,
  index,
}) => {
  if (test.type === 'repair') {
    return (
      <Accordion
        key={index}
        title={`Repair` + (test.testDate ? ` - ${test.testDate}` : '')}
      >
        {!!test.comment && (
          <AccordionItem label="Comments" value={test.comment} />
        )}
      </Accordion>
    );
  }

  const values = testValuesForType(test, AssemblyType[assemblyType]);
  const errors = validateTestResult(
    test,
    AssemblyType[assemblyType],
    rpCheck2Type
  );

  return (
    <Accordion
      title={backflowTestResultTitle({
        testDate: test.testDate,
        assemblyType,
        index,
      })}
      icon={
        <BackflowTestResultStatus
          test={test}
          assemblyType={assemblyType}
          rpCheck2Type={rpCheck2Type}
        />
      }
    >
      {!!values.systemPsi && (
        <AccordionItem required label="System PSI" value={values.systemPsi} />
      )}
      {assemblyType.startsWith('RP') && (
        <RpTestResult test={values as TestResultRp} />
      )}
      {assemblyType.startsWith('DC') && (
        <DcTestResult test={values as TestResultDc} />
      )}
      {assemblyType === 'TYPE_2_BYPASS' && (
        <Type2BypassTestResult test={values as TestResultType2Bypass} />
      )}
      {['PVB', 'SVB'].includes(assemblyType) && (
        <SvbPvbTestResult test={values as TestResultSvbPvb} />
      )}
      {errors.map((error, index) => (
        <Text variant="validation" key={index}>
          {error}
        </Text>
      ))}
    </Accordion>
  );
};

export default BackflowTestResult;
